import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { EntrySkeletonComponent } from '../shared/entry-skeleton/entry-skeleton.component';
import { AuthService, ThemeService } from '../../../core/services';
import { passwordMatchValidator } from '../../../core/utils/password-match-validator';
import { ResetPasswordData } from '../../../core/models';
import { LOGIN_PATH } from '../../../core/constants';
import { FormErrorStateMatcher } from '../../../core/utils/FormErrorStateMatcher';
import { FocusDirective } from '../../main/shared/directives/focus.directive';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { passwordValidator } from '../../../core/utils/password-validator';

@Component({
  selector: 'dbx-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
  host: {
    class: 'entry-page',
  },
  standalone: true,
  imports: [
    EntrySkeletonComponent,
    MatIcon,
    ReactiveFormsModule,
    TranslateModule,
    MatFormField,
    MatInput,
    MatLabel,
    FocusDirective,
    MatButton,
    MatProgressSpinner,
    MatError,
    MatSuffix,
  ],
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly themeService = inject(ThemeService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  showPassword = signal<boolean>(false);
  showConfirm = signal<boolean>(false);
  loading = signal<boolean>(false);
  submitted = signal<boolean>(false);
  resetKey: string = '';

  errorStateMatcher: FormErrorStateMatcher = new FormErrorStateMatcher(this.submitted);

  form = this.formBuilder.group(
    {
      password: ['', [Validators.required, Validators.minLength(10), passwordValidator()]],
      confirm: ['', [Validators.required]],
    },
    {
      validators: passwordMatchValidator('password', 'confirm'),
    },
  );

  get password(): AbstractControl<string | null, string | null> | null {
    return this.form.get('password');
  }

  get confirm(): AbstractControl<string | null, string | null> | null {
    return this.form.get('confirm');
  }

  ngOnInit(): void {
    this.themeService.forceDarkTheme();
    this.route.queryParams.subscribe((params) => {
      this.resetKey = params['resetKey'];
      if (!this.resetKey) {
        this.translateService
          .get('resetPassword.alerts.invalidResetKey')
          .subscribe((message) => this.toastrService.error(message));
        this.router.navigate([LOGIN_PATH]).then();
      }
    });
  }

  ngOnDestroy(): void {
    this.themeService.init();
  }

  submit() {
    this.submitted.set(true);
    if (this.form.invalid) {
      return;
    }
    this.loading.set(true);
    const data: ResetPasswordData = {
      resetKey: this.resetKey,
      password: this.password?.value ?? '',
    };
    this.authService.resetPassword(data).subscribe({
      next: () => {
        this.loading.set(false);
        this.toastrService.success(this.translateService.instant('resetPassword.alerts.updateSuccess'));
        this.router.navigate([LOGIN_PATH]).then();
      },
      error: (error) => {
        this.loading.set(false);
        this.toastrService.error(
          this.translateService.instant(
            error.status === 409 ? 'resetPassword.alerts.invalidResetKey' : 'common.alerts.genericError',
          ),
        );
      },
    });
  }

  togglePassword(): void {
    this.showPassword.update((v) => !v);
  }

  toggleConfirm(): void {
    this.showConfirm.update((v) => !v);
  }
}
